import { cn } from '@/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  memo,
} from 'react'
import { toast } from 'sonner'
import { getCharacterType, getCharacterTypeStyle } from '@/utils/characterUtils'
import { apiService, getMediaUrl } from '@/services/api'
import { MessageBubble } from '../MessageBubble'
import { MessageEmptyState } from '../MessageEmptyState'
import { MessageSkeleton } from '../MessageSkeleton'
import { ScrollToBottomButton } from '../ScrollToBottomButton'
import { useMessageScrollNative } from './useMessageScrollNative'
import { useMessagePaginationNative } from './useMessagePaginationNative'
import type { MessageListProps, ProcessedMessage } from './MessageList.types'
import type { Message, PrivateMessage } from '@/types/message'
import type { Character } from '@/types'
import type { Member as MemberType } from '@/types/member'

/**
 * MessageList — implementação com scroll nativo do navegador.
 *
 * Substitui a versão com react-virtuoso. Razão: virtualização com
 * altura dinâmica + reverse pagination tem race conditions de scroll
 * inicial impossíveis de resolver de forma confiável (alturas
 * estimadas no mount, re-medição quando imagens carregam, etc).
 *
 * Com scroll nativo, o navegador é dono da posição. Usamos:
 * - onScroll com rAF pra detectar isAtBottom
 * - ResizeObserver pra re-colar no fim quando alturas crescem (imagens)
 * - IntersectionObserver no top sentinel pra carregar antigas
 * - useLayoutEffect pra preservar posição visual ao prepend
 *
 * Reintroduzir virtualização só acima de ~500-1000 mensagens em viewport.
 * Com 100 (limite atual do backend), DOM nativo é trivial.
 */
export const MessageListNative = memo(function MessageListNative({
  messages,
  currentUserId,
  chatMode = 'group',
  className,
  characters = [],
  members = [],
  isMaster = false,
  isLoading = false,
  onUpdateMessage,
  onMessagesChange,
  privateConversationId,
  onLoadMore,
  loadingMore = false,
  hasMore = false,
}: MessageListProps) {
  // Estados de edição/exclusão
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null)
  const [editingContent, setEditingContent] = useState('')
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null)
  const editInputRef = useRef<HTMLTextAreaElement>(null)

  // Refs pra detectar nova mensagem vs prepend
  const prevMessagesLengthRef = useRef(messages.length)
  const prevLastMessageIdRef = useRef<string | null>(null)
  const prevFirstMessageIdRef = useRef<string | null>(null)
  // Refs pra preservar posição visual ao prepender mensagens antigas
  const prevScrollHeightRef = useRef(0)

  // Refs do scroll inicial / canal
  const didInitialScrollRef = useRef(false)
  const initialScrollChannelRef = useRef<string | null>(null)
  const [initialScrollDone, setInitialScrollDone] = useState(false)

  // Sentinel pro IntersectionObserver no topo
  const topSentinelRef = useRef<HTMLDivElement | null>(null)

  const {
    scrollerRef,
    isAtBottom,
    newMessagesCount,
    scrollToBottom,
    onScroll,
    handleNewMessage,
    resetState: resetScrollState,
    stickToBottomRef,
  } = useMessageScrollNative({ currentUserId })

  const {
    isLoading: isPaginationLoading,
    handleStartReached,
    resetPagination,
  } = useMessagePaginationNative({
    onLoadMore: async () => {
      if (onLoadMore) {
        await onLoadMore()
        return 1
      }
      return 0
    },
    hasMore,
  })

  // Mapas pra performance
  const charactersById = useMemo(() => {
    const map = new Map<string, Character>()
    characters.forEach(c => map.set(c.id, c))
    return map
  }, [characters])

  const membersByUserId = useMemo(() => {
    const map = new Map<string, MemberType>()
    members.forEach(m => map.set(m.userId, m))
    return map
  }, [members])

  // Personagem (não-NPC) de cada usuário: a conversa exibe SEMPRE o nome do
  // personagem quando o remetente tem um (decisão do Paulo, 2026-07-20) —
  // mesmo em mensagens antigas/enviadas sem character_id.
  const charactersByOwner = useMemo(() => {
    const map = new Map<string, Character>()
    characters.forEach(c => { if (!c.isNpc) map.set(c.userId, c) })
    return map
  }, [characters])

  const compareUserIds = useCallback((a: any, b: any): boolean => {
    return String(a || '').trim() === String(b || '').trim()
  }, [])

  // Backend usa snake_case em private e camelCase em group.
  const isPrivate = chatMode === 'private'

  const getMessageUserId = useCallback((message: Message | PrivateMessage): string => {
    if (isPrivate) {
      const m = message as PrivateMessage
      return m.sender_id || m.user_id || ''
    }
    return (message as Message).userId
  }, [isPrivate])

  const getMessageDisplayName = useCallback((message: Message | PrivateMessage): string => {
    // Ordem: personagem da mensagem (ex.: mestre falando como NPC) → personagem
    // do remetente na mesa → nome do usuário (ex.: mestre sem ficha).
    if (isPrivate) {
      const m = message as PrivateMessage
      const senderId = m.sender_id || m.user_id || ''
      return charactersByOwner.get(senderId)?.name || m.sender_full_name || m.user_full_name || 'Usuário'
    }
    const m = message as Message
    return m.characterName || charactersByOwner.get(m.userId)?.name || m.userFullName
  }, [isPrivate, charactersByOwner])

  const getMessageCharacter = useCallback((message: Message | PrivateMessage): Character | null => {
    if (isPrivate) return null
    const m = message as Message
    return m.characterId ? charactersById.get(m.characterId) || null : null
  }, [isPrivate, charactersById])

  // Chave do canal atual (grupo ou conversa privada específica)
  const channelKey = `${chatMode}:${privateConversationId ?? ''}`

  // Reset ao trocar de canal. useLayoutEffect (não useEffect): precisa rodar
  // ANTES do scroll inicial abaixo (mesma fase, ordem de declaração), senão
  // ao trocar entre conversas com a MESMA contagem de mensagens o ref
  // didInitialScroll continuava true e a nova conversa abria sem scroll pro fim.
  useLayoutEffect(() => {
    resetScrollState()
    resetPagination()
    prevMessagesLengthRef.current = messages.length
    prevLastMessageIdRef.current = messages.length > 0 ? messages[messages.length - 1].id : null
    prevFirstMessageIdRef.current = messages.length > 0 ? messages[0].id : null
    if (initialScrollChannelRef.current !== channelKey) {
      initialScrollChannelRef.current = channelKey
      didInitialScrollRef.current = false
      setInitialScrollDone(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMode, privateConversationId])

  /**
   * Scroll inicial — useLayoutEffect (não useEffect) pra rodar antes
   * do paint. Setamos scrollTop direto no scrollHeight, sem depender
   * de cálculo do virtuoso. ResizeObserver abaixo cobre crescimento
   * pós-mount (imagens, etc).
   */
  useLayoutEffect(() => {
    if (didInitialScrollRef.current) return
    if (messages.length === 0) return
    if (isLoading) return
    const el = scrollerRef.current
    if (!el) return

    el.scrollTop = el.scrollHeight
    didInitialScrollRef.current = true
    // Libera IntersectionObserver depois de pequeno delay, garantindo
    // que o ResizeObserver teve chance de re-colar caso bubbles cresçam.
    const t = window.setTimeout(() => setInitialScrollDone(true), 200)
    return () => window.clearTimeout(t)
    // channelKey nas deps: garante re-execução ao trocar de conversa mesmo
    // quando messages.length não muda (conversas com a mesma contagem).
  }, [messages.length, isLoading, scrollerRef, channelKey])

  /**
   * ResizeObserver no scroller — quando alturas dinâmicas (imagens
   * carregando, markdown re-medindo) fazem o conteúdo crescer, re-cola
   * no fim se o usuário estava no fim ou o scroll inicial ainda não
   * terminou.
   */
  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    const ro = new ResizeObserver(() => {
      // Se o usuário rolou pra cima, ignora.
      if (stickToBottomRef.current || !initialScrollDone) {
        el.scrollTop = el.scrollHeight
      }
    })
    ro.observe(el)
    // Observa também os filhos imediatos (cada bubble pode crescer).
    Array.from(el.children).forEach(child => ro.observe(child as Element))
    return () => ro.disconnect()
  }, [scrollerRef, stickToBottomRef, initialScrollDone, messages.length])

  /**
   * Quando a aba volta a ficar visível, re-snapa pro fim se o usuário
   * estava colado no fim. Cobre o caso em que mensagens chegaram com
   * a aba oculta (ResizeObserver pausa em tabs ocultas em alguns
   * navegadores) ou em que o navegador moveu o scroll por scroll
   * anchoring antes do nosso CSS desabilitar.
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      if (!stickToBottomRef.current) return
      const el = scrollerRef.current
      if (!el) return
      // Próximo paint: garante que o navegador já fez seu re-layout
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight
      })
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [scrollerRef, stickToBottomRef])

  /**
   * IntersectionObserver no top sentinel — substitui startReached do
   * virtuoso. Só ativa após initialScrollDone, evitando carregar
   * antigas durante o scroll inicial.
   */
  useEffect(() => {
    if (!initialScrollDone) return
    if (!hasMore || loadingMore) return
    const sentinel = topSentinelRef.current
    const root = scrollerRef.current
    if (!sentinel || !root) return

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          handleStartReached()
        }
      },
      { root, rootMargin: '200px 0px 0px 0px', threshold: 0 }
    )
    io.observe(sentinel)
    return () => io.disconnect()
  }, [initialScrollDone, hasMore, loadingMore, handleStartReached, scrollerRef])

  /**
   * Detectar novas mensagens (append no fim) E preservar posição em
   * prepend (antigas no início).
   *
   * Usamos useLayoutEffect: o ajuste de scrollTop precisa ocorrer ANTES
   * do paint, senão o usuário vê a lista pular.
   */
  useLayoutEffect(() => {
    const el = scrollerRef.current
    if (!el) return

    const prevLength = prevMessagesLengthRef.current
    const currentLength = messages.length
    const lastMessage = messages[messages.length - 1]
    const lastMessageId = lastMessage?.id ?? null
    const firstMessage = messages[0]
    const firstMessageId = firstMessage?.id ?? null

    if (currentLength > prevLength) {
      const lastChanged = lastMessageId && lastMessageId !== prevLastMessageIdRef.current
      const firstChanged = firstMessageId && firstMessageId !== prevFirstMessageIdRef.current

      if (firstChanged && !lastChanged) {
        // Prepend (antigas carregadas no topo) — preservar posição visual.
        // Usar o scrollTop ATUAL (o usuário rolou até o topo depois do
        // último snapshot; com overflow-anchor:none ele ainda aponta pra
        // posição de antes do prepend).
        const heightDiff = el.scrollHeight - prevScrollHeightRef.current
        if (heightDiff > 0) {
          el.scrollTop += heightDiff
        }
      } else if (lastChanged) {
        // Append (mensagem nova no fim).
        const senderId = getMessageUserId(lastMessage)
        handleNewMessage(senderId)
      }
    }

    // Snapshot pra comparações no próximo render.
    prevMessagesLengthRef.current = currentLength
    prevLastMessageIdRef.current = lastMessageId
    prevFirstMessageIdRef.current = firstMessageId
    prevScrollHeightRef.current = el.scrollHeight
  }, [messages, getMessageUserId, handleNewMessage, scrollerRef])

  // Edição
  const handleEditMessage = useCallback((messageId: string, currentContent: string) => {
    if (chatMode !== 'group' || !isMaster) return
    const message = messages.find(msg => msg.id === messageId)
    if (!message) return
    const messageType = (message as Message).messageType
    if (messageType === 'dice-roll' || messageType === 'dice_roll') {
      toast.error('Rolagens de dados não podem ser editadas')
      return
    }
    if (messageType === 'poll_result') {
      toast.error('Resultados de enquete não podem ser editados')
      return
    }
    setEditingMessageId(messageId)
    setEditingContent(currentContent)
  }, [chatMode, isMaster, messages])

  const handleCancelEdit = useCallback(() => {
    setEditingMessageId(null)
    setEditingContent('')
  }, [])

  const handleSaveEdit = useCallback(async (messageId: string) => {
    if (!editingContent.trim()) {
      toast.error('A mensagem não pode estar vazia')
      return
    }
    const trimmedContent = editingContent.trim()
    try {
      setEditingMessageId(null)
      setEditingContent('')
      if (onUpdateMessage) {
        await onUpdateMessage(messageId, trimmedContent)
        toast.success('Mensagem editada com sucesso')
      } else {
        await apiService.updateMessage(messageId, trimmedContent)
        toast.success('Mensagem editada com sucesso')
      }
      onMessagesChange?.()
    } catch (error: any) {
      console.error('Erro ao editar mensagem:', error)
      setEditingMessageId(messageId)
      setEditingContent(trimmedContent)
      toast.error(error.message || 'Erro ao editar mensagem')
    }
  }, [editingContent, onUpdateMessage, onMessagesChange])

  const handleDeleteMessage = useCallback((messageId: string) => {
    if (chatMode !== 'group' || !isMaster) return
    setDeletingMessageId(messageId)
  }, [chatMode, isMaster])

  const confirmDeleteMessage = useCallback(async () => {
    if (!deletingMessageId) return
    try {
      await apiService.deleteMessage(deletingMessageId)
      onMessagesChange?.()
      toast.success('Mensagem excluída com sucesso')
      setDeletingMessageId(null)
    } catch (error: any) {
      console.error('Erro ao excluir mensagem:', error)
      toast.error(error.message || 'Erro ao excluir mensagem')
    }
  }, [deletingMessageId, onMessagesChange])

  // Foco no textarea ao começar a editar
  useEffect(() => {
    if (editingMessageId && editInputRef.current) {
      editInputRef.current.focus()
      editInputRef.current.select()
    }
  }, [editingMessageId])

  // Processar mensagens pra renderização
  const processedMessages = useMemo((): ProcessedMessage[] => {
    return messages.map((message: Message | PrivateMessage, index: number) => {
      const messageUserId = getMessageUserId(message)
      const character = getMessageCharacter(message)
      const isNpcMessage = character?.isNpc === true

      let isOwnMessage: boolean
      if (chatMode === 'private' && (message as PrivateMessage).is_own_message !== undefined) {
        isOwnMessage = (message as PrivateMessage).is_own_message!
      } else if (isNpcMessage) {
        isOwnMessage = false
      } else {
        isOwnMessage = compareUserIds(messageUserId, currentUserId)
      }

      let characterType: 'mestre' | 'jogador' | 'npc' | null = null
      if (chatMode === 'group') {
        if (character) {
          characterType = getCharacterType(character, membersByUserId)
        } else {
          const member = membersByUserId.get(messageUserId)
          characterType = !member
            ? 'jogador'
            : member.role === 'mestre'
              ? 'mestre'
              : 'jogador'
        }
      }

      const typeStyle = characterType ? getCharacterTypeStyle(characterType) : null

      // Simétrico do `isLastInGroup`, com a MESMA `compareUserIds`: uma
      // segunda comparação de identidade divergiria da primeira no primeiro
      // caso difícil (id de NPC, maiúsculas, tipo diferente).
      const isFirstInGroup =
        index === 0 || !compareUserIds(getMessageUserId(messages[index - 1]), messageUserId)

      return {
        id: message.id,
        isOwnMessage,
        showAvatar: !isOwnMessage && isFirstInGroup,
        isFirstInGroup,
        avatarUrl: character?.imageUrl ? getMediaUrl(character.imageUrl) : undefined,
        isLastInGroup: index === messages.length - 1 || !compareUserIds(getMessageUserId(messages[index + 1]), messageUserId),
        displayContent: message.content,
        displayTimestamp: message.timestamp,
        displayUserName: getMessageDisplayName(message),
        characterType,
        typeStyle,
        messageType: (message as Message).messageType,
        mediaFile: (message as Message).mediaFile,
        editedAt: (message as Message).editedAt,
        is_read: (message as PrivateMessage).is_read,
        diceType: (message as Message).diceType,
        diceTest: (message as Message).diceTest,
        dicePool: (message as Message).dicePool,
        diceResult: (message as Message).diceResult,
      }
    })
  }, [messages, currentUserId, chatMode, charactersById, membersByUserId, compareUserIds, getMessageUserId, getMessageDisplayName, getMessageCharacter])

  // Loading inicial
  if (isLoading && messages.length === 0) {
    return (
      <div className={cn('h-full flex flex-col relative', className)}>
        <div className="p-3 md:p-6">
          <MessageSkeleton count={6} />
        </div>
      </div>
    )
  }

  // Estado vazio
  if (messages.length === 0) {
    return (
      <div className={cn('h-full flex flex-col relative', className)}>
        <div className="p-3 md:p-6 flex-1 flex items-center justify-center">
          <MessageEmptyState chatMode={chatMode} />
        </div>
      </div>
    )
  }

  return (
    <div className={cn('h-full flex flex-col relative', className)}>
      <style>{`
        .native-message-scroller {
          /* Desliga scroll anchoring nativo do navegador.
             Sem isso, quando o conteúdo cresce acima do viewport
             (imagens carregando, voltar de outra aba), o Chrome
             ancora num item arbitrário e move o scroll pro meio.
             Nosso ResizeObserver cuida do snap-to-bottom. */
          overflow-anchor: none;
          scrollbar-width: thin;
          scrollbar-color: rgba(155, 155, 155, 0.6) rgba(0, 0, 0, 0.05);
        }
        .native-message-scroller > * {
          overflow-anchor: none;
        }
        .native-message-scroller::-webkit-scrollbar {
          width: 8px;
          display: block;
        }
        .native-message-scroller::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 4px;
        }
        .native-message-scroller::-webkit-scrollbar-thumb {
          background-color: rgba(155, 155, 155, 0.6);
          border-radius: 4px;
        }
        .native-message-scroller::-webkit-scrollbar-thumb:hover {
          background-color: rgba(155, 155, 155, 0.8);
        }
        .dark .native-message-scroller {
          scrollbar-color: rgba(155, 155, 155, 0.4) rgba(255, 255, 255, 0.05);
        }
        .dark .native-message-scroller::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
        }
        .dark .native-message-scroller::-webkit-scrollbar-thumb {
          background-color: rgba(155, 155, 155, 0.4);
        }
        .dark .native-message-scroller::-webkit-scrollbar-thumb:hover {
          background-color: rgba(155, 155, 155, 0.6);
        }
      `}</style>

      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="flex-1 overflow-y-auto overscroll-contain native-message-scroller"
      >
        <div ref={topSentinelRef} aria-hidden="true" />

        {/* Header de loading antigas */}
        {(loadingMore || isPaginationLoading) && (
          <div className="flex items-center justify-center py-4">
            <div className="flex items-center gap-2 text-sm text-game-muted">
              <div className="h-4 w-4 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
              <span>Carregando mensagens...</span>
            </div>
          </div>
        )}
        {hasMore && !loadingMore && !isPaginationLoading && (
          <div className="flex items-center justify-center py-2">
            <span className="text-xs text-game-muted">Role para cima para ver mensagens anteriores</span>
          </div>
        )}

        {/* Bubbles */}
        {processedMessages.map(message => (
          <div key={message.id} className="px-3 md:px-6">
            <MessageBubble
              ref={editingMessageId === message.id ? editInputRef : null}
              message={message}
              chatMode={chatMode}
              isMaster={isMaster}
              isEditing={editingMessageId === message.id}
              editingContent={editingContent}
              onEdit={handleEditMessage}
              onDelete={handleDeleteMessage}
              onEditChange={setEditingContent}
              onSaveEdit={handleSaveEdit}
              onCancelEdit={handleCancelEdit}
            />
          </div>
        ))}
      </div>

      <ScrollToBottomButton
        show={!isAtBottom || newMessagesCount > 0}
        newMessagesCount={newMessagesCount}
        onClick={() => scrollToBottom('smooth')}
      />

      <AlertDialog
        open={deletingMessageId !== null}
        onOpenChange={(open) => !open && setDeletingMessageId(null)}
      >
        <AlertDialogContent className="bg-game-secondary dark:bg-zinc-900 border-game-border-primary dark:border-zinc-700/50">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-game-primary dark:text-zinc-100">
              Confirmar Exclusão
            </AlertDialogTitle>
            <AlertDialogDescription className="text-game-muted dark:text-zinc-400">
              Tem certeza que deseja excluir esta mensagem? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-game-secondary dark:text-zinc-200 hover:bg-game-hover dark:hover:bg-white/[0.05]">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteMessage}
              className="bg-rose-600 hover:bg-rose-500 text-white"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
})
